import { Tooltip } from '@hagerty/react-components';
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { User } from '../../types/common';
import { getUserDisplayName } from '../../utils/user';
import { Avatar } from '../Avatar';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuContent,
} from '../DropdownMenu';
import styles from './UserMenu.module.scss';

interface UserMenuProps {
  /**
   * User object to use for displaying the user menu
   */
  user?: User;

  /**
   * Array of options to display in the user menu
   */
  options?: ReactNode[];

  /**
   * Node to display when the user is not logged in
   */
  loginLink?: ReactNode;

  /**
   * Determines whether to show the multioffice tooltip
   */
  showMultiofficeTooltip?: boolean;
}

export function UserMenu({
  user,
  options,
  loginLink,
  showMultiofficeTooltip = false,
}: UserMenuProps): JSX.Element {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const tooltipTriggerRef = useRef<HTMLButtonElement>(null);

  const onCloseTooltip = useMemo(
    () =>
      isTooltipVisible
        ? () => {
            setIsTooltipVisible(false);
            localStorage.setItem('multiofficeTooltipDisplayed', 'true');
          }
        : undefined,
    [isTooltipVisible, setIsTooltipVisible]
  );

  useEffect(() => {
    if (
      showMultiofficeTooltip &&
      !isTooltipVisible &&
      localStorage.getItem('multiofficeTooltipDisplayed') !== 'true'
    ) {
      setIsTooltipVisible(true);
    }
  }, [isTooltipVisible, setIsTooltipVisible, showMultiofficeTooltip]);

  const onClickOutside = () => {}; // Do nothing

  return user && options ? (
    <div className={styles['hds-user-menu__container']}>
      <DropdownMenu onOpenChange={onCloseTooltip}>
        <DropdownMenuTrigger
          className={styles['hds-user-menu']}
          id='logged-in-user-menu'
          data-testid='logged-in-user-menu'
          ref={tooltipTriggerRef}
        >
          <div className={styles['hds-user-menu__avatar']}>
            <Avatar src={user.avatar} alt='user avatar' size='xxxs' />
          </div>
          <span className={styles['hds-user-menu__title']}>
            {getUserDisplayName(user)}
          </span>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className={styles['hds-menu-list']}
          align='end'
          alignOffset={-10}
          sideOffset={-5}
        >
          {options.map((option, i) => (
            <DropdownMenuItem
              key={i}
              className={styles['hds-menu-list__item']}
              asChild
            >
              {option}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {showMultiofficeTooltip && (
        <Tooltip
          content={
            <span style={{ whiteSpace: 'nowrap' }}>Switch offices here</span>
          }
          isVisible={isTooltipVisible}
          triggerRef={tooltipTriggerRef}
          onClickOutside={onClickOutside}
          onClose={onCloseTooltip}
          placement='left'
        />
      )}
    </div>
  ) : loginLink ? (
    <div data-testid='login-button' className={styles['hds-user-menu__login']}>
      {loginLink}
    </div>
  ) : null;
}
